import './PopoverSelector.scss';

import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { PopoverSelectorSearch } from './PopoverSelectorSearch';
import { Tooltip } from 'antd';
import { When } from 'react-if';

import { CheckOutlined, LoadingOutlined, SelectOutlined, SettingOutlined } from '@ant-design/icons';
import { haltClickEventPropagation, haltEvent } from '@lib/events';
import React, { useCallback, useEffect, useState } from 'react';

export interface PopoverSelectorProps<T> {
	children: React.ReactNode;
	className?: string;
	footer?: React.ReactElement;
	itemRender: (item: T) => React.ReactNode;
	listItems: Array<T>;
	manageRoute?: string;
	onDeselect: (item: T) => void;
	onOpen?: (id: number) => void;
	onOpenChange?: (visible: boolean) => void;
	onSearch?: (value: string) => void;
	onSelect: (item: T) => void;
	placeholder?: string;
	selectedIds?: Array<number>;
	singleSelect?: boolean;
	trigger?: 'hover' | 'click';
}

export function PopoverSelector<T extends { id: number }>({
	children,
	className,
	footer,
	itemRender,
	listItems,
	manageRoute,
	onDeselect,
	onOpen,
	onOpenChange,
	onSearch,
	onSelect,
	placeholder,
	selectedIds = [],
	singleSelect,
	trigger = 'hover'
}: PopoverSelectorProps<T>): JSX.Element {
	const [ addingId, setAddingId ] = useState(-1);
	const [ removingId, setRemovingId ] = useState(-1);

	const handleOnClick = useCallback(
		(e: React.MouseEvent, isSelected: boolean, item: T) => {
			e.preventDefault();
			e.stopPropagation();

			if (isSelected) {
				if (!singleSelect) {
					setRemovingId(item.id);
				}
				onDeselect(item);
			} else {
				if (!singleSelect) {
					setAddingId(item.id);
				}
				onSelect(item);
			}
		},
		[ onDeselect, onSelect, singleSelect ]
	);

	const handleOpen = useCallback(
		(e: React.MouseEvent, id: number) => {
			e.preventDefault();
			e.stopPropagation();

			if (onOpen) {
				onOpen(id);
			}
		},
		[ onOpen ]
	);

	useEffect(() => {
		if (selectedIds.includes(addingId)) {
			setAddingId(-1);
		}

		if (!selectedIds.includes(removingId)) {
			setRemovingId(-1);
		}
	}, [ addingId, removingId, selectedIds, setAddingId, setRemovingId ]);

	const showSearch = Boolean(onSearch);
	const showManage = Boolean(manageRoute);
	const showHeader = showSearch || showManage;

	return (
		<div className='popover-selector'>
			<Tooltip
				arrow={ false }
				classNames={ { root: classNames('popover-selector-panel', className) } }
				onOpenChange={ onOpenChange }
				placement='bottomRight'
				title={
					<div
						onClick={ haltClickEventPropagation }
						onMouseDown={ haltEvent }
						onMouseUp={ haltEvent }>
						{ showHeader &&
							<div className='popover-header'>
								{ showSearch && <PopoverSelectorSearch
									onSearch={ onSearch }
									placeholder={ placeholder } /> }
								{ showManage &&
									<Link
										className='manager-link'
										to={ manageRoute ?? '' }>
										<SettingOutlined />
									</Link> }
							</div> }
						<div className='selector-list'>
							{ listItems.map((item: T) => {
								const { id } = item;
								const isSelected = selectedIds.includes(id);
								const loading = id === addingId || id === removingId;

								return (
									<div
										className={ `clickable-row ${isSelected || loading ? 'selected' : ''}` }
										key={ id }
										onClick={ (e) => handleOnClick(e, isSelected, item) }>
										<div className='list-item'>
											<div>{ itemRender(item) }</div>
											<When condition={ Boolean(onOpen) }>
												<SelectOutlined
													className='open-item'
													onClick={ (e) => handleOpen(e, id) } />
											</When>
										</div>
										<div className='selected-check'>
											<When condition={ !loading && isSelected }>
												<CheckOutlined />
											</When>
											<When condition={ loading }>
												<LoadingOutlined />
											</When>
										</div>
									</div>
								);
							}) }
						</div>
						{ footer }
					</div>
				}
				trigger={ trigger }>
				{ children }
			</Tooltip>
		</div>
	);
}
